"use client"

import type { MouseEvent } from "react"
import { motion, useReducedMotion } from "motion/react"
import { ScanSearch } from "lucide-react"

import { trackEvent } from "@/lib/analytics"
import { IDENTIFY_CTA, identifyCtaProps } from "@/lib/identify-cta"
import { SunGrassMotif } from "./section-icons"
import { Stagger } from "@/components/motion"
import { EASE_OUT_QUINT } from "@/lib/motion-presets"

function AnimatedMotif() {
  const reduce = useReducedMotion()
  return (
    <motion.div
      className="flex justify-center mb-4"
      initial={reduce ? false : { opacity: 0, scale: 0.92, rotate: -2 }}
      whileInView={reduce ? undefined : { opacity: 0.7, scale: 1, rotate: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: EASE_OUT_QUINT }}
    >
      <SunGrassMotif className="w-[160px] h-auto" />
    </motion.div>
  )
}

export function FinalCta() {
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    trackEvent("cta_clicked", identifyCtaProps(IDENTIFY_CTA.location.homepageFinal))
    document
      .getElementById("analysis-card")
      ?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <section className="bg-linear-to-b from-white to-green-100 px-7 py-16 md:py-[88px]">
      <Stagger className="max-w-2xl mx-auto text-center">
        <Stagger.Item>
          <AnimatedMotif />
        </Stagger.Item>
        <Stagger.Item>
          <h2 className="font-bold tracking-[-0.02em] text-gray-900 mb-4 text-[28px] md:text-4xl">
            Find out what&apos;s actually growing
          </h2>
        </Stagger.Item>
        <Stagger.Item>
          <p className="text-gray-600 leading-relaxed mb-8 text-base md:text-lg">
            One photo. A confidence score. A year-round plan tuned to your zip. No credit card, no spam.
          </p>
        </Stagger.Item>
        <Stagger.Item>
          <a
            href="#analysis-card"
            onClick={handleClick}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-10 py-4 rounded-xl bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold text-lg md:text-xl shadow-[0_12px_32px_rgb(22,163,74,0.28)] transition-[transform,box-shadow,background] duration-300 hover:scale-[1.02] active:scale-[0.98] min-h-[56px]"
          >
            <ScanSearch className="w-5 h-5" />
            {IDENTIFY_CTA.label}
          </a>
        </Stagger.Item>
        <Stagger.Item>
          <p className="mt-4 text-xs text-gray-500">
            Ready in seconds
          </p>
        </Stagger.Item>
      </Stagger>
    </section>
  )
}
